import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../utils/api';

const AnalyticsPage = () => {
  const [period, setPeriod] = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('attorneyToken');
      if (!token) {
        throw new Error('No authentication token found');
      }
      const data = await api.getAttorneyAnalytics(period, token);
      setAnalytics(data);
    } catch (err) {
      setError(err.message || 'Failed to load analytics');
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderBar = (value, max, color = 'primary') => {
    const percentage = max > 0 ? (value / max) * 100 : 0;
    const bgColor = color === 'primary'
      ? 'bg-gradient-to-r from-primary-500 to-secondary-500'
      : color === 'success'
      ? 'bg-green-500'
      : color === 'warning'
      ? 'bg-yellow-500'
      : 'bg-blue-500';

    return (
      <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
        <div
          className={`${bgColor} h-full flex items-center justify-end px-2 text-white text-sm font-semibold transition-all duration-500`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        >
          {percentage > 15 && `${percentage.toFixed(1)}%`}
        </div>
      </div>
    );
  };

  const StatCard = ({ title, value, subtitle, color = 'primary' }) => {
    const colorClasses = {
      primary: 'from-primary-500 to-secondary-500',
      success: 'from-green-400 to-green-600',
      warning: 'from-yellow-400 to-orange-500',
      info: 'from-blue-400 to-blue-600',
    };

    return (
      <div className="card">
        <div className={`text-sm font-semibold text-gray-600 mb-2`}>{title}</div>
        <div className={`text-3xl font-bold bg-gradient-to-r ${colorClasses[color]} bg-clip-text text-transparent mb-1`}>
          {value}
        </div>
        {subtitle && <div className="text-sm text-gray-500">{subtitle}</div>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="card text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="card">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-bold text-center">Error Loading Analytics</h2>
            <p className="text-center mt-2">{error}</p>
          </div>
          <div className="text-center">
            <button onClick={fetchAnalytics} className="btn-primary">
              Retry
            </button>
            <Link to="/attorney/dashboard" className="btn-secondary ml-4">
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const {
    successRates = {},
    patternMatching = {},
    timingStats = {},
    reviewStats = {},
  } = analytics || {};

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent mb-2">
            Attorney Analytics
          </h1>
          <p className="text-gray-600">Performance metrics and settlement insights</p>
        </div>
        <Link to="/attorney/dashboard" className="btn-secondary">
          Back to Dashboard
        </Link>
      </div>

      {/* Period Selector */}
      <div className="card mb-8">
        <div className="flex items-center gap-4">
          <span className="font-semibold text-gray-700">Time Period:</span>
          <div className="flex gap-2">
            {[7, 30, 90].map((days) => (
              <button
                key={days}
                onClick={() => setPeriod(days)}
                className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                  period === days
                    ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {days} Days
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Key Metrics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Overall Success Rate"
          value={`${((successRates.overall || 0) * 100).toFixed(1)}%`}
          subtitle={`${successRates.totalCases || 0} total cases`}
          color="success"
        />
        <StatCard
          title="Pattern Accuracy"
          value={`${((patternMatching.averageAccuracy || 0) * 100).toFixed(1)}%`}
          subtitle="AI prediction quality"
          color="info"
        />
        <StatCard
          title="Avg Resolution Time"
          value={`${timingStats.averageDays || 0} days`}
          subtitle={`Target: ${timingStats.targetDays || 14} days`}
          color="warning"
        />
        <StatCard
          title="Approval Rate"
          value={`${((reviewStats.approvalRate || 0) * 100).toFixed(1)}%`}
          subtitle={`${reviewStats.totalReviewed || 0} cases reviewed`}
          color="primary"
        />
      </div>

      {/* Success Rates by Dispute Type */}
      <div className="card mb-8">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <svg className="w-6 h-6 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Settlement Success Rates by Type
        </h2>

        {successRates.byType && Object.keys(successRates.byType).length > 0 ? (
          <div className="space-y-4">
            {Object.entries(successRates.byType).map(([type, data]) => (
              <div key={type} className="border-l-4 border-primary-500 pl-4">
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <span className="font-semibold text-lg capitalize">{type.replace(/_/g, ' ')}</span>
                    <span className="text-gray-500 ml-2">({data.count || 0} cases)</span>
                  </div>
                  <span className="text-xl font-bold text-primary-600">
                    {((data.successRate || 0) * 100).toFixed(1)}%
                  </span>
                </div>
                {renderBar(data.successRate || 0, 1, 'success')}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No dispute type data available for this period</p>
        )}
      </div>

      {/* Pattern Matching Metrics */}
      <div className="card mb-8">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <svg className="w-6 h-6 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Pattern Matching Performance
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="border rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-2">Average Confidence Score</div>
            <div className="text-3xl font-bold text-blue-600 mb-2">
              {((patternMatching.averageConfidence || 0) * 100).toFixed(1)}%
            </div>
            {renderBar(patternMatching.averageConfidence || 0, 1, 'info')}
          </div>

          <div className="border rounded-lg p-4">
            <div className="text-sm text-gray-600 mb-2">Prediction Accuracy</div>
            <div className="text-3xl font-bold text-green-600 mb-2">
              {((patternMatching.accuracyRate || 0) * 100).toFixed(1)}%
            </div>
            {renderBar(patternMatching.accuracyRate || 0, 1, 'success')}
          </div>
        </div>

        {patternMatching.confidenceVsResults && (
          <div className="mt-6">
            <h3 className="font-semibold mb-4">Confidence vs Actual Results</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 font-semibold text-gray-700">Confidence Range</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">Cases</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">Success Rate</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">Visual</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {Object.entries(patternMatching.confidenceVsResults).map(([range, data]) => (
                    <tr key={range} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{range}</td>
                      <td className="px-4 py-3">{data.count || 0}</td>
                      <td className="px-4 py-3">
                        <span className={`font-semibold ${
                          (data.successRate || 0) > 0.7 ? 'text-green-600' :
                          (data.successRate || 0) > 0.5 ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {((data.successRate || 0) * 100).toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="w-32">
                          {renderBar(data.successRate || 0, 1, 'success')}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Timing Statistics */}
      <div className="card mb-8">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <svg className="w-6 h-6 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Resolution Time Analysis
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Average</div>
            <div className="text-3xl font-bold text-blue-600">{timingStats.averageDays || 0}</div>
            <div className="text-xs text-gray-500">days</div>
          </div>

          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Fastest</div>
            <div className="text-3xl font-bold text-green-600">{timingStats.minDays || 0}</div>
            <div className="text-xs text-gray-500">days</div>
          </div>

          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Longest</div>
            <div className="text-3xl font-bold text-red-600">{timingStats.maxDays || 0}</div>
            <div className="text-xs text-gray-500">days</div>
          </div>

          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Target</div>
            <div className="text-3xl font-bold text-purple-600">{timingStats.targetDays || 14}</div>
            <div className="text-xs text-gray-500">days</div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold">Performance vs Target</span>
            <span className={`text-sm font-bold ${
              (timingStats.averageDays || 0) <= (timingStats.targetDays || 14)
                ? 'text-green-600'
                : 'text-orange-600'
            }`}>
              {(timingStats.averageDays || 0) <= (timingStats.targetDays || 14)
                ? `${((timingStats.targetDays || 14) - (timingStats.averageDays || 0)).toFixed(1)} days ahead of target`
                : `${((timingStats.averageDays || 0) - (timingStats.targetDays || 14)).toFixed(1)} days behind target`
              }
            </span>
          </div>
          <div className="relative w-full h-8 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="absolute h-full bg-gradient-to-r from-green-400 to-green-600"
              style={{ width: `${Math.min(((timingStats.targetDays || 14) / (timingStats.maxDays || 30)) * 100, 100)}%` }}
            >
              <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-white font-semibold">
                Target
              </span>
            </div>
            <div
              className="absolute h-full bg-gradient-to-r from-blue-400 to-blue-600 opacity-75"
              style={{ width: `${Math.min(((timingStats.averageDays || 0) / (timingStats.maxDays || 30)) * 100, 100)}%` }}
            >
              <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-white font-semibold">
                Avg
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Review Statistics */}
      <div className="card mb-8">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <svg className="w-6 h-6 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          Attorney Review Statistics
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="text-center p-4 border-2 border-gray-200 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Total Reviewed</div>
            <div className="text-3xl font-bold text-gray-800">{reviewStats.totalReviewed || 0}</div>
          </div>

          <div className="text-center p-4 border-2 border-green-200 rounded-lg bg-green-50">
            <div className="text-sm text-gray-600 mb-1">Approved</div>
            <div className="text-3xl font-bold text-green-600">{reviewStats.approved || 0}</div>
          </div>

          <div className="text-center p-4 border-2 border-yellow-200 rounded-lg bg-yellow-50">
            <div className="text-sm text-gray-600 mb-1">Modified</div>
            <div className="text-3xl font-bold text-yellow-600">{reviewStats.modified || 0}</div>
          </div>

          <div className="text-center p-4 border-2 border-red-200 rounded-lg bg-red-50">
            <div className="text-sm text-gray-600 mb-1">Rejected</div>
            <div className="text-3xl font-bold text-red-600">{reviewStats.rejected || 0}</div>
          </div>
        </div>

        <div className="mt-6">
          <h3 className="font-semibold mb-4">Review Outcome Distribution</h3>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-green-700">Approved</span>
                <span className="text-sm font-bold text-green-600">
                  {reviewStats.totalReviewed > 0
                    ? ((reviewStats.approved / reviewStats.totalReviewed) * 100).toFixed(1)
                    : 0}%
                </span>
              </div>
              {renderBar(reviewStats.approved || 0, reviewStats.totalReviewed || 1, 'success')}
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-yellow-700">Modified</span>
                <span className="text-sm font-bold text-yellow-600">
                  {reviewStats.totalReviewed > 0
                    ? ((reviewStats.modified / reviewStats.totalReviewed) * 100).toFixed(1)
                    : 0}%
                </span>
              </div>
              {renderBar(reviewStats.modified || 0, reviewStats.totalReviewed || 1, 'warning')}
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium text-red-700">Rejected</span>
                <span className="text-sm font-bold text-red-600">
                  {reviewStats.totalReviewed > 0
                    ? ((reviewStats.rejected / reviewStats.totalReviewed) * 100).toFixed(1)
                    : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                <div
                  className="bg-red-500 h-full flex items-center justify-end px-2 text-white text-sm font-semibold transition-all duration-500"
                  style={{
                    width: `${reviewStats.totalReviewed > 0
                      ? Math.min(((reviewStats.rejected || 0) / reviewStats.totalReviewed) * 100, 100)
                      : 0}%`
                  }}
                >
                  {reviewStats.totalReviewed > 0 && ((reviewStats.rejected || 0) / reviewStats.totalReviewed) * 100 > 15
                    ? `${(((reviewStats.rejected || 0) / reviewStats.totalReviewed) * 100).toFixed(1)}%`
                    : ''
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex justify-center gap-4">
        <Link to="/attorney/dashboard" className="btn-primary px-8">
          Back to Dashboard
        </Link>
        <button onClick={fetchAnalytics} className="btn-secondary px-8">
          Refresh Data
        </button>
      </div>
    </div>
  );
};

export default AnalyticsPage;
